import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ServiceProvider from "../../models/serviceProvider";
import serviceProviderService from "../../services/serviceProviderService";
import formHelper from "../../util/formHelper";
import AddressEdit from "../../components/common/AddressEdit";
import Address from "../../models/address";
import ModalWindow from "../../components/common/ModalWindow";
import ValidationManager from "../../components/common/validationManager";
import Validator from "../../components/common/Validator";
import PaymentTerm from "../../models/paymentTerm";
import SubscriptionContextModel from "../../contexts/subscriptionContextModel";
import SubscriptionContext from "../../contexts/subscriptionContext";
import User from "../../models/user";
import UserContext from "../../contexts/userContext";
import DatePicker from "react-datepicker";
import Page from "../../components/work/layout/Page";

const validator = {
  serviceProviderName: "serviceProviderName",
  paymentTerm: "paymentTerm",
  serviceProviderCode: "serviceProviderCode",
  insuranceExpirationDate: "insuranceExpirationDate",
  phone: "phone",
  emailAddresses: "emailAddresses",
  contactPersonName: "contactPersonName",
  contactPersonEmail: "contactPersonEmail",
  contactPersonPhone: "contactPersonPhone",

  //trades: "trades",
};

const ServiceProviderEdit: React.FC = () => {
  const { id: idParam } = useParams();
  const navigate = useNavigate();

  const user = useContext<User | undefined>(UserContext);
  const { subscription } =
    useContext<SubscriptionContextModel>(SubscriptionContext);

  const [isBusy, setIsBusy] = useState<boolean>(false);

  const [isNew, setIsNew] = useState<boolean>(true);
  const [showDeleteWindow, setShowDeleteWindow] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");

  const [serviceProvider, setServiceProvider] = useState<ServiceProvider>(
    new ServiceProvider(),
  );
  const [validationManager] = useState<ValidationManager>(
    new ValidationManager(),
  );

  useEffect(() => {
    const initEdit = async (idServiceProvider: number) => {
      try {
        const { data: serviceProvider } =
          await serviceProviderService.getServiceProviderById(
            idServiceProvider,
          );

        if (serviceProvider) {
          setServiceProvider(serviceProvider);
        }

        validationManager.setIsValid(validator.serviceProviderName, true);
        validationManager.setIsValid(validator.paymentTerm, true);
        //validationManager.setIsValid(validator.serviceArea, true);
        validationManager.setIsValid(validator.serviceProviderCode, true);
        validationManager.setIsValid(validator.insuranceExpirationDate, true);

        validationManager.setIsValid(validator.phone, true);
        validationManager.setIsValid(validator.emailAddresses, true);
        validationManager.setIsValid(validator.contactPersonName, true);
        validationManager.setIsValid(validator.contactPersonEmail, true);
        validationManager.setIsValid(validator.contactPersonPhone, true);
        //validationManager.setIsValid(validator.trades, true);
      } catch (error) {
        // TODO: show error with toastr
      }
    };

    const isNew = idParam === "new";
    setIsNew(isNew);

    if (!isNew) {
      const idServiceProvider = Number(idParam);
      initEdit(idServiceProvider);
    }
  }, [idParam, validationManager]);

  // const onTradesChange = (newTrades: Trade[]) => {
  //   const newServiceProvider = {
  //     ...serviceProvider,
  //     trades: newTrades,
  //   };
  //   setServiceProvider(newServiceProvider);
  //   const isValid = newTrades.length !== 0;
  //   validationManager.setIsValid(validator.trades, isValid);
  // };

  const onChange = (event: any) => {
    let { id, name, value } = formHelper.getDataFromEvent(event.target);

    if (id === validator.serviceProviderCode) {
      if (value) {
        value = (value as string).toUpperCase();
      }
    }

    const newServiceProvider = { ...serviceProvider, [name]: value };
    setServiceProvider(newServiceProvider);

    if (id === validator.serviceProviderName) {
      const isValid = value.length !== 0;
      validationManager.setIsValid(validator.serviceProviderName, isValid);
    }

    if (id === validator.serviceProviderCode) {
      const isValid = value.length !== 0;
      validationManager.setIsValid(validator.serviceProviderCode, isValid);
    }

    if (id === validator.phone) {
      const isValid = value.length !== 0;
      validationManager.setIsValid(validator.phone, isValid);
    }

    if (id === validator.emailAddresses) {
      const isValid = value.length !== 0;
      validationManager.setIsValid(validator.emailAddresses, isValid);
    }

    if (id === validator.contactPersonEmail) {
      const isValid = value.length !== 0;
      validationManager.setIsValid(validator.contactPersonEmail, isValid);
    }

    if (id === validator.contactPersonName) {
      const isValid = value.length !== 0;
      validationManager.setIsValid(validator.contactPersonName, isValid);
    }

    if (id === validator.contactPersonPhone) {
      const isValid = value.length !== 0;
      validationManager.setIsValid(validator.contactPersonPhone, isValid);
    }

    if (id === validator.insuranceExpirationDate) {
      const isValid = event.length !== 0;
      validationManager.setIsValid(validator.insuranceExpirationDate, isValid);
    }
  };

  const onAddressChange = (address: Address) => {
    const newVector = { ...serviceProvider, address: address };
    setServiceProvider(newVector);
  };

  const onSubmit = async (event: any) => {
    event.preventDefault();

    setErrorMessage("");

    if (!validationManager.validate()) {
      return;
    }

    try {
      if (isNew) {
        setIsBusy(true);

        if (user) {
          const newServiceProvider = { ...serviceProvider };
          setServiceProvider(newServiceProvider);

          await serviceProviderService.createServiceProvider(
            subscription.idSubscription,
            serviceProvider,
          );
          setIsBusy(false);
        }
      } else {
        setIsBusy(true);
        await serviceProviderService.updateServiceProvider(serviceProvider);
        setIsBusy(false);
      }
    } catch (error: any) {
      setIsBusy(false);
      const errorMessage = error.response.data;
      setErrorMessage(errorMessage);
      return;
    }

    navigate("/work/service-providers");
  };

  const onDeleteConfirm = () => {
    setShowDeleteWindow(true);
  };

  const onDeleteOk = () => {
    setShowDeleteWindow(false);
    onDelete();
  };

  const onDeleteCancel = () => {
    setShowDeleteWindow(false);
  };

  const onDelete = async () => {
    setIsBusy(true);
    await serviceProviderService.deleteServiceProvider(serviceProvider);
    setIsBusy(false);
    navigate("/work/service-providers");
  };

  const onCancel = () => {
    navigate("/work/service-providers");
  };

  const onPaymentTermChanged = (event: any) => {
    const newValue: PaymentTerm = event.target.value;

    if (newValue !== PaymentTerm.None) {
      const newServiceProvider = { ...serviceProvider, paymentTerm: newValue };
      setServiceProvider(newServiceProvider);
    }

    validationManager.setIsValid(validator.paymentTerm, true);
  };

  // const onServiceAreaChanged = (event: any) => {
  //   const newValue: ServiceArea = event.target.value;

  //   if (newValue !== ServiceArea.None) {
  //     const newServiceProvider = { ...serviceProvider, serviceArea: newValue };
  //     setServiceProvider(newServiceProvider);
  //   }
  //   validationManager.setIsValid(validator.serviceArea, true);
  // };

  // const onDispatchMethodChanged = (event: any) => {
  //   const newValue: DispatchMethod = event.target.value;

  //   if (newValue !== DispatchMethod.None) {
  //     const newServiceProvider = {
  //       ...serviceProvider,
  //       dispatchMethod: newValue,
  //     };
  //     setServiceProvider(newServiceProvider);
  //   }
  //   validationManager.setIsValid(validator.dispatchMethod, true);
  // };

  const onDispatchInstructionsChange = (event: any) => {
    //const { id, name, value } = formHelper.getDataFromEvent(event.target);
    const { name, value } = formHelper.getDataFromEvent(event.target);
    const newServiceProvider = { ...serviceProvider, [name]: value };
    setServiceProvider(newServiceProvider);
  };

  return (
    <Page
      pageTitle={(isNew ? "Create" : "Edit") + " Service Provider"}
      breadcrumbs={[
        { linkName: "Service Providers", linkUlr: "/work/service-providers" },
        { linkName: isNew ? "Create" : "Edit", linkUlr: "" },
      ]}
    >
      <section className="section">
        <div className="card p-4">
          <form
            className="row g-3"
            onSubmit={onSubmit}
            onKeyDown={formHelper.onEnterKeyDownPreventSubmit}
          >
            <div className="col-4">
              <Validator
                name={validator.serviceProviderName}
                errorMessage="Please enter a Service Provider Name."
                validationManager={validationManager}
              >
                <div>
                  <label htmlFor="serviceProviderName" className="form-label">
                    Service Provider Name
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="serviceProviderName"
                    name={formHelper.nameOf<ServiceProvider>(
                      (t) => t.serviceProviderName,
                    )}
                    value={serviceProvider.serviceProviderName}
                    onChange={onChange}
                  />
                </div>
              </Validator>
            </div>

            <div className="col-md-4">
              <Validator
                name={validator.serviceProviderCode}
                errorMessage="Please enter a Service Provider Code."
                validationManager={validationManager}
              >
                <div>
                  <label htmlFor="serviceProviderCode" className="form-label">
                    Service Provider Code
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="serviceProviderCode"
                    name={formHelper.nameOf<ServiceProvider>(
                      (t) => t.serviceProviderCode,
                    )}
                    value={serviceProvider.serviceProviderCode}
                    onChange={onChange}
                  />
                </div>
              </Validator>
            </div>

            <div
              className="d-flex align-items-end col-md-4"
              style={{ marginBottom: "10px" }}
            >
              <label
                className="form-check-label"
                htmlFor="serviceProviderIsPublic"
              >
                Is Public
              </label>
              <div className="form-switch form-switch-right-text">
                <input
                  className="form-check-input form-switch-right-text"
                  type="checkbox"
                  id="serviceProviderIsPublic"
                  name={formHelper.nameOf<ServiceProvider>((t) => t.isPublic)}
                  checked={serviceProvider.isPublic}
                  onChange={onChange}
                />
              </div>
            </div>

            <div>
              <label className="form-label">
                <b>Address</b>
              </label>
            </div>

            <div>
              <AddressEdit
                inputAddress={serviceProvider.address}
                onChange={onAddressChange}
                isNew={isNew}
                validationManager={validationManager}
                validationId="address"
              />
            </div>

            <div className="col-sm-6">
              <Validator
                name={validator.paymentTerm}
                errorMessage="Please select a payment term."
                validationManager={validationManager}
              >
                <div>
                  <label htmlFor="paymentTerm" className="form-label">
                    Payment Term
                  </label>
                  <div>
                    <select
                      className="form-select"
                      id="paymentTerm"
                      name={formHelper.nameOf<ServiceProvider>(
                        (t) => t.paymentTerm,
                      )}
                      aria-label="select"
                      value={
                        serviceProvider.paymentTerm === undefined
                          ? PaymentTerm.None
                          : serviceProvider.paymentTerm
                      }
                      onChange={onPaymentTermChanged}
                    >
                      {Object.keys(PaymentTerm).map((key, idx) => (
                        <option key={idx} value={key}>
                          {formHelper.getEnumDisplayValue(PaymentTerm, key)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </Validator>
            </div>

            <div className="col-md-6">
              <Validator
                name={validator.insuranceExpirationDate}
                errorMessage="Please enter a Insurance Expiration Date."
                validationManager={validationManager}
              >
                <div>
                  <label
                    htmlFor="insuranceExpirationDate"
                    className="form-label"
                  >
                    Insurance Expiration Date
                  </label>
                  <div className="calendar-textbox-full-width">
                    <DatePicker
                      className="form-control"
                      id="insuranceExpirationDate"
                      name={formHelper.nameOf<ServiceProvider>(
                        (t) => t.insuranceExpirationDate,
                      )}
                      selected={serviceProvider.insuranceExpirationDate}
                      onChange={(event) => onChange(event)}
                      popperPlacement="left"
                    />
                  </div>
                </div>
              </Validator>
            </div>

            <div className="col-md-6">
              <Validator
                name={validator.emailAddresses}
                errorMessage="Please enter an email."
                validationManager={validationManager}
              >
                <div>
                  <label htmlFor="emailAddresses" className="form-label">
                    Service Provider Email
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="emailAddresses"
                    name={formHelper.nameOf<ServiceProvider>((t) => t.email)}
                    value={serviceProvider.email}
                    onChange={onChange}
                  />
                </div>
              </Validator>
            </div>

            <div className="col-md-6">
              <Validator
                name={validator.phone}
                errorMessage="Please enter a phone number."
                validationManager={validationManager}
              >
                <div>
                  <label htmlFor="phone" className="form-label">
                    Service Provider Phone#
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="phone"
                    name={formHelper.nameOf<ServiceProvider>((t) => t.phone)}
                    value={serviceProvider.phone}
                    onChange={onChange}
                  />
                </div>
              </Validator>
            </div>

            <div>
              <label className="form-label">
                <b>Dispatch Option</b>
              </label>
            </div>

            <div className="d-flex">
              <div className="form-check col-md-4">
                <div>
                  <label className="form-check-label" htmlFor="isEmailDispatch">
                    Email Dispatch
                  </label>
                  <input
                    className="form-check-input"
                    type="checkbox"
                    name={formHelper.nameOf<ServiceProvider>(
                      (t) => t.isEmailDispatch,
                    )}
                    checked={serviceProvider.isEmailDispatch}
                    id="isEmailDispatch"
                    onChange={onChange}
                  />
                </div>
              </div>

              <div className="form-check col-md-4">
                <div>
                  <label className="form-check-label" htmlFor="isSmsDispatch">
                    SMS Dispatch
                  </label>
                  <input
                    className="form-check-input"
                    type="checkbox"
                    name={formHelper.nameOf<ServiceProvider>(
                      (t) => t.isSmsDispatch,
                    )}
                    checked={serviceProvider.isSmsDispatch}
                    id="isSmsDispatch"
                    onChange={onChange}
                  />
                </div>
              </div>

              <div className="form-check col-md-4">
                <div>
                  <label
                    className="form-check-label"
                    htmlFor="isPortalDispatch"
                  >
                    Portal Dispatch
                  </label>
                  <input
                    className="form-check-input"
                    type="checkbox"
                    name={formHelper.nameOf<ServiceProvider>(
                      (t) => t.isPortalDispatch,
                    )}
                    checked={serviceProvider.isPortalDispatch}
                    id="isPortalDispatch"
                    onChange={onChange}
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="form-label">
                <b>Contact Person / Technician Information</b>
              </label>
            </div>

            <div className="d-flex">
              <div className="col-md-4">
                <Validator
                  name={validator.contactPersonName}
                  errorMessage="Please enter a name."
                  validationManager={validationManager}
                >
                  <div>
                    <label htmlFor="contactPersonName" className="form-label">
                      Name
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="contactPersonName"
                      name={formHelper.nameOf<ServiceProvider>(
                        (t) => t.contactPersonName,
                      )}
                      value={serviceProvider.contactPersonName}
                      onChange={onChange}
                    />
                  </div>
                </Validator>
              </div>

              <div className="col-md-4">
                <Validator
                  name={validator.contactPersonEmail}
                  errorMessage="Please enter an email."
                  validationManager={validationManager}
                >
                  <div>
                    <label htmlFor="contactPersonEmail" className="form-label">
                      Email
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="contactPersonEmail"
                      name={formHelper.nameOf<ServiceProvider>(
                        (t) => t.contactPersonEmail,
                      )}
                      value={serviceProvider.contactPersonEmail}
                      onChange={onChange}
                    />
                  </div>
                </Validator>
              </div>

              <div className="col-md-4">
                <Validator
                  name={validator.contactPersonPhone}
                  errorMessage="Please enter a phone number."
                  validationManager={validationManager}
                >
                  <div>
                    <label htmlFor="contactPersonPhone" className="form-label">
                      Phone #
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="contactPersonPhone"
                      name={formHelper.nameOf<ServiceProvider>(
                        (t) => t.contactPersonPhone,
                      )}
                      value={serviceProvider.contactPersonPhone}
                      onChange={onChange}
                    />
                  </div>
                </Validator>
              </div>
            </div>

            <div className="col-12">
              <label htmlFor="dispatchInstructions" className="form-label">
                Service Instructions
              </label>
              <textarea
                className="form-control"
                id="dispatchInstructions"
                name={formHelper.nameOf<ServiceProvider>(
                  (t) => t.dispatchInstructions,
                )}
                value={
                  serviceProvider.dispatchInstructions == null
                    ? ""
                    : serviceProvider.dispatchInstructions
                }
                onChange={onDispatchInstructionsChange}
                style={{ height: "100px" }}
              />
            </div>

            {/* <div className="col-sm-6">
                  <Validator
                    name={validator.serviceArea}
                    errorMessage="Please select a service area."
                    validationManager={validationManager}
                  >
                    <div>
                      <label htmlFor="serviceArea" className="form-label">
                        Service Area
                      </label>
                      <select
                        className="form-select"
                        id="serviceArea"
                        name={formHelper.nameOf<ServiceProvider>(
                          (t) => t.serviceArea
                        )}
                        aria-label="select"
                        value={
                          serviceProvider.serviceArea === undefined
                            ? ServiceArea.None
                            : serviceProvider.serviceArea
                        }
                        onChange={onServiceAreaChanged}
                      >
                        {Object.keys(ServiceArea).map((key, idx) => (
                          <option key={idx} value={key}>
                            {formHelper.getEnumDisplayValue(ServiceArea, key)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </Validator>
                </div> */}

            {/* <div className="col-12">
                  <Validator
                    name={validator.trades}
                    errorMessage="Please select a trade."
                    validationManager={validationManager}
                  >
                    <div>
                      <Trades
                        trades={serviceProvider.trades}
                        controlLabel="Trades"
                        onTradeChanged={onTradesChange}
                      />
                    </div>
                  </Validator>
                </div> */}

            <div className="col-12">
              <div className="d-flex justify-content-between">
                <div>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isBusy}
                  >
                    {isNew ? "Create" : "Save"}
                  </button>{" "}
                  <button
                    type="reset"
                    className="btn btn-secondary"
                    onClick={() => onCancel()}
                  >
                    Cancel
                  </button>
                </div>
                {!isNew && (
                  <div>
                    <button
                      type="reset"
                      className="btn btn-danger"
                      disabled={isBusy}
                      onClick={() => onDeleteConfirm()}
                    >
                      Delete
                    </button>
                  </div>
                )}
              </div>
            </div>

            {errorMessage !== "" && (
              <div className="row text-center">
                <div className="error-message">{errorMessage}</div>
              </div>
            )}
          </form>
        </div>
      </section>
      {showDeleteWindow && (
        <ModalWindow title="Delete" onOk={onDeleteOk} onCancel={onDeleteCancel}>
          <div>{`Are you sure you want to delete the Service Provider?`}</div>
        </ModalWindow>
      )}
    </Page>
  );
};

export default ServiceProviderEdit;
